import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { ActionType, AppDispatch } from "../../reducer";
import { DeleteBlockAction, InsertBlockAction } from "../../reducer/page";
import { useAppIdMap } from "../app";
import {
  usePageBlocks,
  useSelectedNotebookPage,
  useSelectedNotebookPageIdMap,
} from "../page";
import { NoteBlock } from "../../types/notebook/note/block";
import { NotebookPage, NotePageData } from "../../types/note";
import { useEffect, useMemo, useState } from "react";
import { movePageBlock, pageSearch } from "../../api/page";
import { debounce } from "lodash";
import { deleteData } from "../../api";
import { App } from "../../types/app";

export const useInsertBlock = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { appId } = useAppIdMap();
  const { "page-id": pageIdParam } = useParams();
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const pageId = pageIdParam ?? selectedNotebookPageId;

  const insertBlock = (config: { blockIndex: number; block?: NoteBlock }) => {
    if (!appId || !pageId) {
      throw new Error("MUST_HAVE_APP");
    }

    const { blockIndex: index, block } = config;
    const action: InsertBlockAction = {
      type: ActionType.INSERT_BLOCK,
      index,
      appId,
      pageId,
      block,
    };
    dispatch(action as any);
  };

  return insertBlock;
};

export const useMoveBlockPageOptions = (config: { isOpen: boolean }) => {
  const { isOpen } = config;
  const { appId } = useAppIdMap();
  const [state, setState] = useState<{
    pages: NotebookPage[];
    isLoading: boolean;
    searchString: string;
    selectedAppId: string | undefined;
  }>({
    pages: [],
    isLoading: false,
    searchString: "",
    selectedAppId: appId,
  });
  const { pages, isLoading, searchString, selectedAppId } = state;

  const setSelectedAppId = (selectedAppId: string | undefined) =>
    setState((state) => ({ ...state, selectedAppId }));

  const setSearchString = (searchString: string) =>
    setState((state) => ({ ...state, searchString }));

  const setPages = (pages: NotebookPage[]) =>
    setState((state) => ({ ...state, pages }));

  const setIsLoading = (isLoading: boolean) =>
    setState((state) => ({ ...state, isLoading }));

  const runSearch = useMemo(
    () =>
      debounce((searchString: string) => {
        setIsLoading(true);
        pageSearch({
          "app-id": selectedAppId!,
          "search-string": searchString,
          limit: 10,
        })
          .then((r) => {
            setPages(r);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }, 200),
    [selectedAppId],
  );

  useEffect(() => {
    setSelectedAppId(appId);
  }, [appId]);

  useEffect(() => {
    setPages([]);
  }, [selectedAppId]);

  useEffect(() => {
    if (!isOpen) return;
    runSearch(searchString);
  }, [searchString, selectedAppId, isOpen]);

  // useEffect(() => {
  //   runSearch(searchString);
  // }, []);

  return {
    pages,
    isLoading,
    searchString,
    setSearchString,
    setSelectedAppId,
    selectedAppId,
  };
};

export const useMoveBlock = () => {
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const { appId } = useAppIdMap();
  const { deleteBlockState } = useDeleteBlock();
  const { pageBlocks } = usePageBlocks(selectedNotebookPageId);

  return (config: { blockIndex: number; page: NotebookPage }) => {
    const { blockIndex, page } = config;
    const block = (pageBlocks ?? [])[blockIndex];
    movePageBlock({
      "app-id": appId!,
      "block-id": block["block-id"],
      "page-id": page["page-id"],
    });
    deleteBlockState(blockIndex);
  };
};

export const useDeleteBlock = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const { pageBlocks } = usePageBlocks(selectedNotebookPageId);

  const deleteBlockState = (focusedIndex: number) => {
    const action: DeleteBlockAction = {
      type: ActionType.DELETE_BLOCK,
      focusedIndex,
    };
    dispatch(action as any);
  };

  const deleteBlock = (focusedIndex: number) => {
    if (!selectedNotebookPageId) {
      throw new Error("No Page!");
    }
    const block = (pageBlocks ?? [])[focusedIndex];
    deleteData(block);
    deleteBlockState(focusedIndex);
  };

  return { deleteBlock, deleteBlockState };
};
